import * as React from 'react';

import { Messages } from '~src/codegen/Localize';

import { IAdminProfileDetails, IAdminProfileMetadata, IAdminProfileMetadataUser } from '../models/IAdminProfile';
import { AdminProfileStyles } from '../styles/AdminProfileStyles';

export interface AdminProfileHeaderProps {
  profile: IAdminProfileDetails;
}

function formatTimestamp(isoString: string): string {
  const d = new Date(isoString);
  const [date] = d.toISOString().split('T');
  const time = d.toTimeString().slice(0, 8);
  return `${date} at ${time}`;
}

function parseUsername(username: string): { localName: string, domainName: string } {
  const atIndex = username.lastIndexOf('@');
  if (atIndex >= 0) {
    return { localName: username.substring(0, atIndex), domainName: username.substring(atIndex + 1) };
  }
  return { localName: username, domainName: 'local' };
}

function renderUserLink(user: IAdminProfileMetadataUser | undefined): JSX.Element {
  if (!user) {
    return <span>{Messages.adminProfileHeaderMetadataUserNotFound()}</span>;
  }
  const { localName, domainName } = parseUsername(user.username);
  const href = `/#/server/user/${encodeURIComponent(domainName)}/${encodeURIComponent(localName)}/settings`;
  return (
    <a className={AdminProfileStyles.MetadataUserLink} href={href}>
      {user.name}
    </a>
  );
}

function MetadataContent({ metadata }: { metadata: IAdminProfileMetadata }): JSX.Element {
  const showModified = metadata.createdAt !== metadata.updatedAt;

  return (
    <>
      {Messages.adminProfileHeaderMetadataCreatedBy()}{' '}
      {renderUserLink(metadata.createdBy)}{' '}
      {Messages.adminProfileHeaderMetadataOn({ TIMESTAMP: formatTimestamp(metadata.createdAt) })}
      {showModified && (
        <>
          {' | '}
          {Messages.adminProfileHeaderMetadataModifiedBy()}{' '}
          {renderUserLink(metadata.updatedBy)}{' '}
          {Messages.adminProfileHeaderMetadataOn({ TIMESTAMP: formatTimestamp(metadata.updatedAt) })}
        </>
      )}
    </>
  );
}

/**
 * Header component for admin profile details
 * Displays profile name, description, metadata, and action buttons
 */
export const AdminProfileHeader: React.FC<AdminProfileHeaderProps> = ({
  profile,
}) => {
  return (
    <div className={AdminProfileStyles.DetailHeader}>
      <h2 className={AdminProfileStyles.DetailTitle}>
        {profile.name}
        {profile.isSystemProfile && (
          <span className={`${AdminProfileStyles.Badge} ${AdminProfileStyles.SystemBadge} ${AdminProfileStyles.HeaderBadgeSpacing}`}>
            {Messages.adminProfileHeaderSystemBadge()}
          </span>
        )}
      </h2>
      {profile.description && (
        <div className={AdminProfileStyles.DetailDescription}>
          {profile.description}
        </div>
      )}

      {profile.metadata && (
        <div className={AdminProfileStyles.HeaderMetadata}>
          <MetadataContent metadata={profile.metadata} />
        </div>
      )}
    </div>
  );
};

AdminProfileHeader.displayName = 'AdminProfileHeader';
